import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DnsConfig, Domain, SitesResponse } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Authorizations } from "~/api/types";
import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { messageFromError } from "~/helpers/messageFromError.ts";
import { tryCatch } from "~/helpers/tryCatch.ts";

export type SitesListWithAuth = SitesResponse & {
  authorizations?: Authorizations;
};
type DomainWithUserConfig = Domain & { user_config: DnsConfig };

export function useManualPoller(task: () => Promise<void>, interval = 3000) {
  const timeoutRef = useRef<number | null>(null);
  const isPollingRef = useRef(false);
  const [isPolling, setIsPolling] = useState(false);

  const tick = useCallback(async () => {
    await task();

    if (!isPollingRef.current) return;

    timeoutRef.current = window.setTimeout(tick, interval);
  }, [task, interval]);

  const start = useCallback(() => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    setIsPolling(true);
    tick();
  }, [tick]);

  const stop = useCallback(() => {
    isPollingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsPolling(false);
  }, []);

  return { start, stop, isPolling, isPollingRef };
}

async function fetchDomainBase(
  queryClient: QueryClient,
  domainId: string | number,
  useCache: boolean,
): Promise<Domain> {
  const key = domainKeys.detail(domainId);
  const cached = useCache ? queryClient.getQueryData<Domain>(key) : undefined;
  if (cached) return cached;

  const { data } = await apiClient.domains.domainsDetail(Number(domainId));

  return data?.domain;
}

async function fetchDnsConfig(domainId: string | number): Promise<DnsConfig> {
  const { data } = await apiClient.domains.getDomains(Number(domainId));

  return data?.dns_config ?? [];
}

export function useDomainWithDns(domainId: string | number) {
  const queryClient = useQueryClient();
  const firstRunRef = useRef(true);

  useEffect(() => {
    firstRunRef.current = true;
  }, [domainId]);

  const domainKey = domainKeys.detail(domainId);
  const dnsKey = domainKeys.dns(domainId);

  const domainQuery = useQuery({
    queryKey: domainKey,
    enabled: true,
    placeholderData: (prev) => prev,
    queryFn: () => fetchDomainBase(queryClient, domainId, firstRunRef.current),
  });

  const dnsQuery = useQuery({
    queryKey: dnsKey,
    enabled: true,
    queryFn: () => fetchDnsConfig(domainId),
  });

  const isFetching =
    domainQuery.fetchStatus === "fetching" ||
    dnsQuery.fetchStatus === "fetching";
  const isPending = domainQuery.isPending || dnsQuery.isPending;
  const isError = domainQuery.isError || dnsQuery.isError;
  const error = domainQuery.error ?? dnsQuery.error;

  const showInitialLoader = firstRunRef.current && isFetching;

  const data = useMemo<DomainWithUserConfig | undefined>(() => {
    const domain = domainQuery.data;
    if (!domain) return undefined;

    const hasUserConfig =
      Array.isArray(domain.user_config) && domain.user_config.length > 0;
    const shouldUseUserConfig = hasUserConfig || domain.status.dns;

    return {
      ...domain,
      user_config: shouldUseUserConfig
        ? (domain.user_config ?? [])
        : (dnsQuery.data ?? []),
    };
  }, [domainQuery.data, dnsQuery.data]);

  const refetch = async () => {
    await Promise.all([domainQuery.refetch(), dnsQuery.refetch()]);
  };

  // disable firstRun after 1st fetch is over
  useEffect(() => {
    if (
      firstRunRef.current &&
      domainQuery.status === "success" &&
      dnsQuery.status === "success" &&
      domainQuery.fetchStatus !== "fetching" &&
      dnsQuery.fetchStatus !== "fetching"
    ) {
      firstRunRef.current = false;
    }
  }, [
    domainQuery.status,
    dnsQuery.status,
    domainQuery.fetchStatus,
    dnsQuery.fetchStatus,
  ]);

  return {
    data,
    isPending,
    isError,
    error,
    isFetching,
    refetch,
    showInitialLoader,
  };
}

export const useSites = (domainId: number) => {
  return useQuery({
    queryKey: domainKeys.sites(domainId),
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.sitesList(
          {
            "page[size]": 1000,
            domain_id: domainId,
          },
          {
            headers: {
              "Authorize-Collection-Actions": "create",
            },
          },
        ),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });

        throw result.error;
      }

      return result.data.data as SitesListWithAuth;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
